// 4x6 cards are postcards: their backs are authored landscape (6x4) while fronts are
// portrait, so an exact-orientation dimension match comes up empty for them.
export const isPostcardDimensions = (dimensions: Readonly<{ width: number; height: number }>): boolean => {
  return (dimensions.width === 4 && dimensions.height === 6) || (dimensions.width === 6 && dimensions.height === 4);
};

type SelectBackTemplatesArgs<T> = Readonly<{
  exactOrientation: ReadonlyArray<T>;
  rotatedOrientation: ReadonlyArray<T>;
  /** The exact-orientation query has completed (not merely returned no data yet). */
  exactQueryCompleted: boolean;
  isPostcardSize: boolean;
}>;

// Exact-orientation backs always win. Rotated backs apply only for postcard sizes, and only
// once the exact query has completed empty — while it is still loading, rotated results must
// not win (they could get pinned onto new drafts by the placeholder-default effect).
export const selectBackTemplates = <T>(args: SelectBackTemplatesArgs<T>): ReadonlyArray<T> => {
  const { exactOrientation, rotatedOrientation, exactQueryCompleted, isPostcardSize } = args;
  if (exactOrientation.length > 0) return exactOrientation;
  if (!isPostcardSize || !exactQueryCompleted) return [];
  return rotatedOrientation;
};

/**
 * Hoist the layouts a product actually includes ahead of the ones it has to sell.
 *
 * Relative order inside each group is preserved, so `TEMPLATE_ORDER` — and its blank-last rule —
 * still decides everything else. Without a predicate, or on a product where nothing is premium,
 * the list comes back in the order it went in.
 *
 * Keyed off the premium predicate rather than a second "put this one first" prop so the two
 * cannot disagree: whichever layout the free product includes is the one that leads the grid,
 * by construction.
 */
export const orderIncludedFirst = <T>(templates: ReadonlyArray<T>, isPremium?: (template: T) => boolean): T[] => {
  if (!isPremium) return [...templates];
  const included = templates.filter(template => !isPremium(template));
  if (included.length === templates.length) return [...templates];
  return [...included, ...templates.filter(template => isPremium(template))];
};
